import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../supabaseClient';
import { compressListingPhoto, compressedExt } from '../lib/imageCompress';
import {
  checkTitleQuality,
  checkDescriptionQuality,
  checkPhotoCount,
  PHOTOS_MIN,
  TITLE_MIN,
  DESCRIPTION_MIN,
} from '../lib/listingQualityGate';
import './ListPropertyPage.css';

// Derive the seller name from the profile. The listing form must never let
// users type a different name into contact_name — display name on a listing
// is always the current profile name. Empty result triggers the "complete
// your profile" blocker upstream.
function resolveProfileName(profile) {
  if (!profile) return '';
  const full = (profile.full_name || '').trim();
  if (full) return full;
  const first = (profile.first_name || '').trim();
  const last  = (profile.last_name  || '').trim();
  const combined = `${first} ${last}`.trim();
  return combined;
}

// Convert a quality-gate result code into a localized error message.
// Codes come from src/lib/listingQualityGate.js and map to keys under
// listProperty.errors in the translations file.
function gateMsg(t, result, kind) {
  if (!result || result.ok) return null;
  const key = `listProperty.errors.${result.code}`;
  let msg = t(key);
  if (typeof msg !== 'string') return t(`listProperty.errors.${kind}`);
  if (result.need !== undefined)    msg = msg.replace('{min}',     String(result.need));
  if (result.have !== undefined)    msg = msg.replace('{have}',    String(result.have));
  if (result.missing !== undefined) msg = msg.replace('{missing}', String(result.missing));
  if (result.missing !== undefined) msg = msg.replace('{plural}',  result.missing === 1 ? '' : 's');
  return msg;
}

// Renders nothing; on mount sends the user to /login with a redirect param
// so login/signup can return them here on success.
function RedirectToLogin({ to }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(`/login?redirect=${encodeURIComponent(to)}`, { replace: true });
  }, [navigate, to]);
  return null;
}

// Which fields are shown and required for each property type (step 2)
const FIELD_CONFIG = {
  House:                { plotSize: 'optional', size: 'required', beds: 'required', baths: 'required' },
  Apartment:            { plotSize: 'hidden',   size: 'required', beds: 'required', baths: 'required' },
  Villa:                { plotSize: 'optional', size: 'required', beds: 'required', baths: 'required' },
  Land:                 { plotSize: 'required', size: 'hidden',   beds: 'hidden',   baths: 'hidden'   },
  Commercial:           { plotSize: 'optional', size: 'required', beds: 'hidden',   baths: 'optional' },
  'Under Construction': { plotSize: 'optional', size: 'optional', beds: 'optional', baths: 'optional' },
};
const DEFAULT_CONFIG = { plotSize: 'optional', size: 'optional', beds: 'optional', baths: 'optional' };

const EMPTY_FORM = {
  listingType: 'sale',
  propertyType: '',
  title: '',
  price: '',
  city: '',
  region: '',
  address: '',
  plotSize: '',
  size: '',
  bedrooms: '',
  bathrooms: '',
  description: '',
  features: '',
  contactPhone: '',
  contactEmail: '',
  agencyName: '',
};

export default function ListPropertyPage({ editId = null }) {
  const isEdit = !!editId;
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [lastListingId, setLastListingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [photos, setPhotos] = useState([]);            // new uploads (File previews)
  const [existingImages, setExistingImages] = useState([]); // urls already on the listing (edit mode)
  const [submitting, setSubmitting] = useState(false);
  const [hydrating, setHydrating] = useState(isEdit);  // loading existing listing in edit mode
  const [brokerProfile, setBrokerProfile] = useState(null);
  const [coords, setCoords] = useState({ lat: 35.1264, lng: 33.4299 });
  const [hasGeocoded, setHasGeocoded] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [epcRating, setEpcRating] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const timelineFillRef = useRef(null);
  const stepRefs = useRef([]);
  const animatedSteps = useRef(new Set());

  const cfg = FIELD_CONFIG[form.propertyType] || DEFAULT_CONFIG;
  const profileName = resolveProfileName(profile);
  const profileNameMissing = !profileName;

  // Hydrate from DB in edit mode
  useEffect(() => {
    if (!isEdit || !user?.id) return;
    let cancelled = false;
    setHydrating(true);
    supabase
      .from('listings')
      .select('*')
      .eq('id', editId)
      .eq('user_id', user.id)
      .single()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          navigate('/my-listings');
          return;
        }
        setForm({
          listingType:  data.price_label === 'per month' ? 'rent' : 'sale',
          propertyType: data.type || '',
          title:        data.title || '',
          price:        data.price ?? '',
          city:         data.city || '',
          region:       data.area || '',
          address:      data.address || '',
          plotSize:     data.plot_size ?? '',
          size:         data.sqm ?? '',
          bedrooms:     data.beds ?? '',
          bathrooms:    data.baths ?? '',
          description:  data.description || '',
          features:     (data.features || []).join(', '),
          contactPhone: data.contact_phone || '',
          contactEmail: data.contact_email || '',
          agencyName:   data.agency_name || '',
        });
        setExistingImages(data.images || []);
        setEpcRating(data.epc_rating || null);
        setShowMap(data.show_map ?? true);
        if (data.lat != null && data.lng != null) {
          const lat = parseFloat(data.lat);
          const lng = parseFloat(data.lng);
          if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
            setCoords({ lat, lng });
            setHasGeocoded(true);
          }
        }
        setHydrating(false);
      });
    return () => { cancelled = true; };
  }, [isEdit, editId, user?.id, navigate]);

  // Clear fields that are hidden for the newly selected property type
  useEffect(() => {
    if (!form.propertyType) return;
    const c = FIELD_CONFIG[form.propertyType];
    if (!c) return;
    setForm(prev => ({
      ...prev,
      ...(c.plotSize === 'hidden' && { plotSize: '' }),
      ...(c.size    === 'hidden' && { size: '' }),
      ...(c.beds    === 'hidden' && { bedrooms: '' }),
      ...(c.baths   === 'hidden' && { bathrooms: '' }),
    }));
  }, [form.propertyType]); // eslint-disable-line

  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('id, agency_name, is_broker, partner_status')
      .eq('id', user.id)
      .eq('is_broker', true)
      .eq('partner_status', 'approved')
      .maybeSingle()
      .then(({ data }) => { if (data) setBrokerProfile(data); });
  }, [user]);

  useEffect(() => {
    const address = form.address;
    if (!address || address.length < 6) return;
    const timer = setTimeout(async () => {
      try {
        const q = encodeURIComponent(address + ', ' + (form.city || 'Cyprus'));
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1&countrycodes=cy`);
        const data = await res.json();
        if (data?.[0]) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);
          if (lat >= 34.5 && lat <= 35.7 && lng >= 32.2 && lng <= 34.6) {
            setCoords({ lat, lng });
            setHasGeocoded(true);
          }
        }
      } catch (e) {
        console.warn('Geocoding failed', e);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [form.address, form.city]);

  useEffect(() => {
    if (hydrating) return;
    if (!showMap) return;
    if (!mapRef.current) return;
    if (!document.getElementById('leaflet-css-lp')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css-lp';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    const L = window.L || require('leaflet');
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    });
    mapInstanceRef.current = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      attribution: '© OpenStreetMap © CARTO', subdomains: 'abcd', maxZoom: 19,
    }).addTo(mapInstanceRef.current);
    mapInstanceRef.current.setView([coords.lat, coords.lng], 14);
    markerRef.current = L.marker([coords.lat, coords.lng], { draggable: true }).addTo(mapInstanceRef.current);
    markerRef.current.on('dragend', (e) => {
      const pos = e.target.getLatLng();
      setCoords({ lat: pos.lat, lng: pos.lng });
    });
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    };
  }, [step, hydrating, showMap]); // eslint-disable-line

  useEffect(() => {
    if (!mapInstanceRef.current || !markerRef.current) return;
    markerRef.current.setLatLng([coords.lat, coords.lng]);
    mapInstanceRef.current.setView([coords.lat, coords.lng], 15, { animate: true });
  }, [coords]);

  const update = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files);
    const previews = files.map(f => ({ file: f, url: URL.createObjectURL(f), name: f.name }));
    const remainingSlots = Math.max(0, 10 - existingImages.length - photos.length);
    setPhotos(prev => [...prev, ...previews].slice(0, prev.length + remainingSlots));
  };

  const removePhoto = (i) => setPhotos(prev => prev.filter((_, idx) => idx !== i));
  const removeExisting = (i) => setExistingImages(prev => prev.filter((_, idx) => idx !== i));

  const totalPhotoCount = existingImages.length + photos.length;

  // Title gate — empty check first so an empty form shows the right copy,
  // then min-length + gibberish via the shared quality-gate helper.
  const titleError = () => {
    if (!form.title.trim()) return t('listProperty.errors.title');
    const r = checkTitleQuality(form.title);
    return r.ok ? null : gateMsg(t, r, 'title');
  };

  // Description gate — same shape as titleError.
  const descriptionError = () => {
    if (!form.description.trim()) return t('listProperty.errors.description');
    const r = checkDescriptionQuality(form.description);
    return r.ok ? null : gateMsg(t, r, 'description');
  };

  // Photo gate — uses the 3-minimum threshold from listingQualityGate.
  const photosError = () => {
    const r = checkPhotoCount(totalPhotoCount);
    return r.ok ? null : gateMsg(t, r, 'photos');
  };

  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (!form.propertyType) e.propertyType = t('listProperty.errors.propertyType');
      const tErr = titleError(); if (tErr) e.title = tErr;
      if (!form.price) e.price = t('listProperty.errors.price');
    }
    if (step === 2) {
      if (!form.city.trim()) e.city = t('listProperty.errors.city');
      if (!form.address.trim()) e.address = t('listProperty.errors.address');
      const c = FIELD_CONFIG[form.propertyType] || DEFAULT_CONFIG;
      if (c.beds    === 'required' && !form.bedrooms)  e.bedrooms  = t('listProperty.errors.bedrooms');
      if (c.baths   === 'required' && !form.bathrooms) e.bathrooms = t('listProperty.errors.bathrooms');
      if (c.size    === 'required' && !form.size)      e.size      = t('listProperty.errors.size');
      if (c.plotSize === 'required' && !form.plotSize) e.plotSize  = t('listProperty.errors.plotSize');
    }
    if (step === 3) {
      const dErr = descriptionError(); if (dErr) e.description = dErr;
      const pErr = photosError();      if (pErr) e.photos      = pErr;
    }
    if (step === 4) {
      if (profileNameMissing) e.contactName = t('listProperty.errors.contactName');
      if (!form.contactPhone.trim()) e.contactPhone = t('listProperty.errors.contactPhone');
      if (!form.contactEmail.includes('@')) e.contactEmail = t('listProperty.errors.contactEmail');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateAllSteps = () => {
    const e = {};
    if (!form.propertyType) e.propertyType = t('listProperty.errors.propertyType');
    const tErr = titleError();       if (tErr) e.title       = tErr;
    if (!form.price)         e.price        = t('listProperty.errors.price');
    if (!form.city.trim())   e.city         = t('listProperty.errors.city');
    if (!form.address.trim()) e.address     = t('listProperty.errors.address');
    const c = FIELD_CONFIG[form.propertyType] || DEFAULT_CONFIG;
    if (c.beds     === 'required' && !form.bedrooms)  e.bedrooms  = t('listProperty.errors.bedrooms');
    if (c.baths    === 'required' && !form.bathrooms) e.bathrooms = t('listProperty.errors.bathrooms');
    if (c.size     === 'required' && !form.size)      e.size      = t('listProperty.errors.size');
    if (c.plotSize === 'required' && !form.plotSize)  e.plotSize  = t('listProperty.errors.plotSize');
    const dErr = descriptionError(); if (dErr) e.description = dErr;
    const pErr = photosError();      if (pErr) e.photos      = pErr;
    if (profileNameMissing)  e.contactName  = t('listProperty.errors.contactName');
    if (!form.contactPhone.trim()) e.contactPhone = t('listProperty.errors.contactPhone');
    if (!form.contactEmail.includes('@')) e.contactEmail = t('listProperty.errors.contactEmail');
    setErrors(e);
    // If validation failed during final submit, jump back to the earliest step
    // where the error lives so the user sees the inline message in context.
    if (Object.keys(e).length > 0) {
      if (e.propertyType || e.title || e.price) setStep(1);
      else if (e.city || e.address || e.bedrooms || e.bathrooms || e.size || e.plotSize) setStep(2);
      else if (e.description || e.photos) setStep(3);
      else if (e.contactName || e.contactPhone || e.contactEmail) setStep(4);
    }
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);
  const goToStep = (s) => { if (s < step) setStep(s); };

  // Scroll-driven timeline fill
  useEffect(() => {
    if (step >= 5 || hydrating) return;
    const handleScroll = () => {
      if (!timelineFillRef.current) return;
      const doc = document.documentElement;
      const max = (doc.scrollHeight || 0) - window.innerHeight;
      const scrollY = window.scrollY || doc.scrollTop || 0;
      const progress = max > 0 ? Math.min(1, Math.max(0, scrollY / max)) : 0;
      timelineFillRef.current.style.height = (progress * 100) + '%';
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll();
    const t1 = setTimeout(handleScroll, 100);
    const t2 = setTimeout(handleScroll, 500);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [step, hydrating]);

  // Entrance animations (one-time per step). In edit mode, also re-run after
  // hydration so the observer attaches to the now-mounted section refs —
  // without this, every scrollStep stays at opacity:0 and the page looks blank.
  useEffect(() => {
    if (step >= 5 || hydrating) return;

    // Defensive: if any sections are already in view at mount time, reveal
    // them immediately so we don't depend on an Observer callback that may
    // not fire (e.g. in edit mode where the first section is already at
    // the top of the viewport).
    stepRefs.current.forEach((el) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const visible = rect.top < window.innerHeight && rect.bottom > 0;
      if (visible) {
        const stepNum = parseInt(el.dataset.step);
        el.classList.add('in-view');
        if (!animatedSteps.current.has(stepNum)) {
          animatedSteps.current.add(stepNum);
          const info = el.querySelector('.scrollStep__info');
          const fields = el.querySelector('.scrollStep__fields');
          if (info) info.classList.add('animate-in');
          if (fields) fields.classList.add('animate-in');
        }
      }
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const stepEl = entry.target;
        const stepNum = parseInt(stepEl.dataset.step);
        if (entry.isIntersecting) {
          stepEl.classList.add('in-view');
          document.querySelectorAll('.scrollStep').forEach(s => {
            if (parseInt(s.dataset.step) < stepNum) s.classList.add('completed');
          });
          if (!animatedSteps.current.has(stepNum)) {
            animatedSteps.current.add(stepNum);
            const info = stepEl.querySelector('.scrollStep__info');
            const fields = stepEl.querySelector('.scrollStep__fields');
            setTimeout(() => info && info.classList.add('animate-in'), 50);
            setTimeout(() => fields && fields.classList.add('animate-in'), 200);
          }
        } else {
          stepEl.classList.remove('in-view');
        }
      });
    }, {
      threshold: 0.25,
      rootMargin: '-10% 0px -30% 0px',
    });
    stepRefs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [step, hydrating]);

  const handleSubmit = async () => {
    if (!validateAllSteps()) return;
    // Profile name is the single source of truth — never trust form state for
    // this field, even after readonly + validation. If somehow the profile is
    // empty at submit time, abort and send the user to /profile.
    const submitContactName = resolveProfileName(profile);
    if (!submitContactName) {
      setStep(4);
      setErrors(prev => ({ ...prev, contactName: t('listProperty.errors.contactName') }));
      return;
    }
    setSubmitting(true);
    try {
      // Upload any newly added photos. Compress client-side first: ~1600px
      // longest edge, ~0.8 quality, WebP when supported. Drops typical phone
      // photos from multi-MB to a few hundred KB.
      const newImageUrls = [];
      for (const photo of photos) {
        const compressed = await compressListingPhoto(photo.file);
        const ext = compressedExt(compressed);
        const fileName = `${user?.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('listing-images')
          .upload(fileName, compressed, {
            cacheControl: '3600',
            upsert: false,
            contentType: compressed.type || undefined,
          });
        if (uploadError) {
          console.error('Upload error:', uploadError);
          alert('Photo upload failed: ' + uploadError.message);
          setSubmitting(false);
          return;
        }
        const { data: urlData } = supabase.storage
          .from('listing-images')
          .getPublicUrl(fileName);
        if (urlData?.publicUrl) newImageUrls.push(urlData.publicUrl);
      }

      const allImages = [...existingImages, ...newImageUrls];

      const payload = {
        title:         form.title,
        price:         Number(form.price),
        price_label:   form.listingType === 'rent' ? 'per month' : null,
        city:          form.city,
        area:          form.region || null,
        address:       form.address || null,
        type:          form.propertyType,
        beds:          Number(form.bedrooms) || null,
        baths:         Number(form.bathrooms) || null,
        sqm:           Number(form.size) || null,
        plot_size:     Number(form.plotSize) || null,
        description:   form.description,
        features:      form.features ? form.features.split(',').map(f => f.trim()).filter(Boolean) : [],
        images:        allImages,
        contact_name:  submitContactName,
        contact_phone: form.contactPhone,
        contact_email: form.contactEmail,
        agency_name:   brokerProfile?.agency_name || form.agencyName || null,
        lat:           showMap && hasGeocoded ? coords.lat : null,
        lng:           showMap && hasGeocoded ? coords.lng : null,
        show_map:      showMap,
        epc_rating:    epcRating || null,
      };

      if (isEdit) {
        const { error } = await supabase
          .from('listings')
          .update(payload)
          .eq('id', editId)
          .eq('user_id', user.id);
        if (error) {
          console.error('Supabase update error:', error);
          alert('Failed to update: ' + error.message);
          setSubmitting(false);
          return;
        }
        setLastListingId(editId);
      } else {
        const { data, error } = await supabase
          .from('listings')
          .insert({
            ...payload,
            user_id:     user?.id,
            is_featured: false,
            is_boosted:  false,
          })
          .select();
        if (error) {
          console.error('Supabase insert error:', error);
          alert('Failed to submit: ' + error.message);
          setSubmitting(false);
          return;
        }
        const newId = data?.[0]?.id;
        if (newId) {
          setLastListingId(newId);
          localStorage.setItem('last_listing_id', newId);
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      alert('Something went wrong. Please try again.');
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  if (!user) {
    return <RedirectToLogin to={isEdit ? `/edit-listing/${editId}` : '/list-property'} />;
  }

  if (hydrating) {
    return (
      <div className="list-page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'white', fontSize: 16 }}>{t('listProperty.loadingListing')}</div>
      </div>
    );
  }

  if (submitted) {
    if (isEdit) {
      return (
        <div className="list-page">
          <div className="list-page__success">
            <div className="list-success__icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h2>{t('listProperty.editSuccessTitle')}</h2>
            <p>{t('listProperty.editSuccessSub')}</p>
            <button className="list-success__btn" onClick={() => navigate('/my-listings')}>
              {t('listProperty.backToMyListings')}
            </button>
            <button
              className="list-success__btn"
              style={{ background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb', marginTop: 8 }}
              onClick={() => navigate(`/property/${lastListingId}`)}
            >
              {t('listProperty.viewListing')}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="list-page">
        <div className="list-page__success">
          <div className="list-success__icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h2>{t('listProperty.successTitle')}</h2>
          <p>{t('listProperty.successSub')}</p>
          <div style={{ margin: '24px 0', padding: '20px', background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.08))', borderRadius: 14, border: '1px solid rgba(99,102,241,0.2)', textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{t('listProperty.boostTitle')}</div>
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>{t('listProperty.boostSub')}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {[
                { label: '1 Listing · 14 days', price: '€10.99' },
              ].map(p => (
                <div key={p.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#374151', padding: '6px 0', borderBottom: '1px solid rgba(99,102,241,0.1)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    {p.label}
                  </span>
                  <strong style={{ color: '#6366f1' }}>{p.price}</strong>
                </div>
              ))}
            </div>
            <button
              className="list-success__btn"
              style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              onClick={() => navigate(`/boost-select?package=boost_single&preselect=${lastListingId || ''}`)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/></svg>
              {t('listProperty.boostCta')}
            </button>
          </div>
          <button className="list-success__btn" style={{ background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb' }} onClick={() => navigate('/')}>
            {t('listProperty.skipForNow')}
          </button>
        </div>
      </div>
    );
  }

  const steps = t('listProperty.steps');
  const headerTitle = isEdit ? t('listProperty.editTitle') : t('listProperty.title');
  const headerSub   = isEdit ? t('listProperty.editSub')   : t('listProperty.sub');
  const submitBtnLabel = isEdit
    ? (submitting ? t('listProperty.saving') : t('listProperty.saveChanges'))
    : (submitting ? t('listProperty.submitting') : t('listProperty.submitListing'));

  const allPreviewPhotos = [
    ...existingImages,
    ...photos.map(p => p.url),
  ];

  return (
    <div className={`list-page ${step < 5 ? 'list-page--scroll' : ''}`}>
      <div className={`list-page__card ${step < 5 ? 'list-page__card--scroll' : ''}`}>
        {step === 5 && (
          <>
            <div className="list-page__header">
              <h1 className="list-page__title">{headerTitle}</h1>
              <p className="list-page__sub">{headerSub}</p>
            </div>
            <div className="list-progress">
              {steps.map((label, i) => (
                <div key={i} className={`list-progress__step ${step > i + 1 ? 'done' : ''} ${step === i + 1 ? 'active' : ''}`} onClick={() => goToStep(i + 1)} style={{ cursor: i + 1 < step ? 'pointer' : 'default' }}>
                  <div className="list-progress__dot">
                    {step > i + 1
                      ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      : i + 1}
                  </div>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="list-form">

          {step < 5 && (
            <div className="scrollForm">

              <div className="scrollForm__timeline" aria-hidden="true">
                <div className="scrollForm__timelineTrack">
                  <div className="scrollForm__timelineFill" ref={timelineFillRef}></div>
                </div>
              </div>

              {/* STEP 1 — info LEFT, fields RIGHT */}
              <section
                className="scrollStep"
                data-step="1"
                ref={el => stepRefs.current[0] = el}
              >
                <div className="scrollStep__dot"></div>

                <div className="scrollStep__info">
                  <div className="scrollStep__number">Step 1 of 5</div>
                  <h2 className="scrollStep__title">{t('listProperty.step1Title')}</h2>
                  <p className="scrollStep__desc">{t('listProperty.step1Desc')}</p>
                  <div className="scrollStep__tips">
                    <div className="scrollStep__tipsLabel">{t('listProperty.step1TipsLabel')}</div>
                    <ul className="scrollStep__tipList">
                      <li>{t('listProperty.step1Tip1')}</li>
                      <li>{t('listProperty.step1Tip2')}</li>
                      <li>{t('listProperty.step1Tip3')}</li>
                    </ul>
                  </div>
                </div>

                <div className="scrollStep__fields">
                  <div className="scrollStep__card">
                    <div className="list-toggle">
                      {['sale', 'rent'].map(lType => (
                        <button key={lType} className={`list-toggle__btn ${form.listingType === lType ? 'active' : ''}`} onClick={() => update('listingType', lType)}>
                          {lType === 'sale' ? t('listProperty.forSale') : t('listProperty.forRent')}
                        </button>
                      ))}
                    </div>
                    <div className="list-field">
                      <label>{t('listProperty.propertyType')}</label>
                      <select value={form.propertyType} onChange={e => update('propertyType', e.target.value)} className={errors.propertyType ? 'error' : ''}>
                        <option value="">{t('listProperty.selectType')}</option>
                        <option value="House">House</option>
                        <option value="Apartment">Apartment</option>
                        <option value="Villa">Villa</option>
                        <option value="Land">Land</option>
                        <option value="Commercial">Commercial</option>
                        <option value="Under Construction">Under Construction</option>
                      </select>
                      {errors.propertyType && <span className="list-field__err">{errors.propertyType}</span>}
                    </div>
                    <div className="list-field">
                      <label>{t('listProperty.listingTitle')}</label>
                      <input type="text" placeholder="e.g. Modern 3-Bed Apartment in Limassol" value={form.title} onChange={e => update('title', e.target.value)} className={errors.title ? 'error' : ''} />
                      {errors.title && <span className="list-field__err">{errors.title}</span>}
                    </div>
                    <div className="list-field">
                      <label>{form.listingType === 'rent' ? t('listProperty.monthlyRent') : t('listProperty.askingPrice')}</label>
                      <input type="number" placeholder={form.listingType === 'rent' ? 'e.g. 1500' : 'e.g. 450000'} value={form.price} onChange={e => update('price', e.target.value)} className={errors.price ? 'error' : ''} />
                      {errors.price && <span className="list-field__err">{errors.price}</span>}
                    </div>
                  </div>
                </div>
              </section>

              {/* STEP 2 — fields LEFT, info RIGHT */}
              <section
                className="scrollStep scrollStep--reversed scrollStep--longForm"
                data-step="2"
                ref={el => stepRefs.current[1] = el}
              >
                <div className="scrollStep__dot"></div>

                <div className="scrollStep__fields">
                  <div className="scrollStep__card">
                    <div className="list-row">
                      <div className="list-field">
                        <label>{t('listProperty.city')}</label>
                        <input type="text" placeholder="e.g. Limassol" value={form.city} onChange={e => update('city', e.target.value)} className={errors.city ? 'error' : ''} />
                        {errors.city && <span className="list-field__err">{errors.city}</span>}
                      </div>
                      <div className="list-field">
                        <label>{t('listProperty.regionDistrict')}</label>
                        <input type="text" placeholder="e.g. Germasogeia" value={form.region} onChange={e => update('region', e.target.value)} />
                      </div>
                    </div>
                    <div className="list-field">
                      <label>{t('listProperty.fullAddress')}</label>
                      <input type="text" placeholder="Street, Number, Postcode" value={form.address} onChange={e => update('address', e.target.value)} className={errors.address ? 'error' : ''} />
                      {errors.address && <span className="list-field__err">{errors.address}</span>}
                    </div>
                    <div className="list-field">
                      <label className="map-toggle">
                        <input
                          type="checkbox"
                          checked={showMap}
                          onChange={e => setShowMap(e.target.checked)}
                          className="map-toggle__input"
                          role="switch"
                          aria-checked={showMap}
                        />
                        <span className="map-toggle__track" aria-hidden="true">
                          <span className="map-toggle__thumb" />
                        </span>
                        <span className="map-toggle__label">Pin exact location on map</span>
                        <span className={`map-toggle__state ${showMap ? 'map-toggle__state--on' : 'map-toggle__state--off'}`}>
                          {showMap ? 'ON' : 'OFF'}
                        </span>
                      </label>
                      <small className="list-field__hint" style={{ marginTop: 6 }}>
                        {showMap
                          ? 'Buyers will see your exact pinned location on the listing page.'
                          : 'No map will be shown on your listing. The address you entered above is still saved.'}
                      </small>
                    </div>
                    {showMap && (
                      <div className="list-field">
                        <label className="list-field__label">Pin the exact location</label>
                        <small className="list-field__hint list-field__hint--map">
                          {hasGeocoded ? 'Drag the pin if needed for accuracy.' : 'Type the address above to auto-place the pin.'}
                        </small>
                        <div className="listForm__mapWrapper" ref={mapRef} style={{ height: 280, borderRadius: 12, marginTop: 8 }} />
                      </div>
                    )}
                    {(cfg.plotSize !== 'hidden' || cfg.size !== 'hidden') && (
                      <div className="list-row">
                        {cfg.plotSize !== 'hidden' && (
                          <div className="list-field">
                            <label>
                              {t('listProperty.plotSize')}
                              {cfg.plotSize !== 'required' && <span className="list-field__hint">{t('listProperty.optional')}</span>}
                            </label>
                            <input type="number" placeholder="e.g. 500" value={form.plotSize} onChange={e => update('plotSize', e.target.value)} className={errors.plotSize ? 'error' : ''} />
                            {errors.plotSize && <span className="list-field__err">{errors.plotSize}</span>}
                          </div>
                        )}
                        {cfg.size !== 'hidden' && (
                          <div className="list-field">
                            <label>
                              {t('listProperty.coveredArea')}
                              {cfg.size !== 'required' && <span className="list-field__hint">{t('listProperty.optional')}</span>}
                            </label>
                            <input type="number" placeholder="e.g. 120" value={form.size} onChange={e => update('size', e.target.value)} className={errors.size ? 'error' : ''} />
                            {errors.size && <span className="list-field__err">{errors.size}</span>}
                          </div>
                        )}
                      </div>
                    )}
                    {(cfg.beds !== 'hidden' || cfg.baths !== 'hidden') && (
                      <div className="list-row">
                        {cfg.beds !== 'hidden' && (
                          <div className="list-field">
                            <label>
                              {t('listProperty.bedrooms')}
                              {cfg.beds !== 'required' && <span className="list-field__hint">{t('listProperty.optional')}</span>}
                            </label>
                            <select value={form.bedrooms} onChange={e => update('bedrooms', e.target.value)} className={errors.bedrooms ? 'error' : ''}>
                              <option value="">{t('listProperty.select')}</option>
                              {[1,2,3,4,5,'6+'].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            {errors.bedrooms && <span className="list-field__err">{errors.bedrooms}</span>}
                          </div>
                        )}
                        {cfg.baths !== 'hidden' && (
                          <div className="list-field">
                            <label>
                              {t('listProperty.bathrooms')}
                              {cfg.baths !== 'required' && <span className="list-field__hint">{t('listProperty.optional')}</span>}
                            </label>
                            <select value={form.bathrooms} onChange={e => update('bathrooms', e.target.value)} className={errors.bathrooms ? 'error' : ''}>
                              <option value="">{t('listProperty.select')}</option>
                              {[1,2,3,'4+'].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            {errors.bathrooms && <span className="list-field__err">{errors.bathrooms}</span>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="scrollStep__info">
                  <div className="scrollStep__number">Step 2 of 5</div>
                  <h2 className="scrollStep__title">{t('listProperty.step2Title')}</h2>
                  <p className="scrollStep__desc">{t('listProperty.step2Desc')}</p>
                  <div className="scrollStep__tips">
                    <div className="scrollStep__tipsLabel">{t('listProperty.step2TipsLabel')}</div>
                    <ul className="scrollStep__tipList">
                      <li>{t('listProperty.step2Tip1')}</li>
                      <li>{t('listProperty.step2Tip2')}</li>
                      <li>{t('listProperty.step2Tip3')}</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* STEP 3 — info LEFT, fields RIGHT */}
              <section
                className="scrollStep scrollStep--longForm"
                data-step="3"
                ref={el => stepRefs.current[2] = el}
              >
                <div className="scrollStep__dot"></div>

                <div className="scrollStep__info">
                  <div className="scrollStep__number">Step 3 of 5</div>
                  <h2 className="scrollStep__title">{t('listProperty.step3Title')}</h2>
                  <p className="scrollStep__desc">{t('listProperty.step3Desc')}</p>
                  <div className="scrollStep__tips">
                    <div className="scrollStep__tipsLabel">{t('listProperty.step3TipsLabel')}</div>
                    <ul className="scrollStep__tipList">
                      <li>{t('listProperty.step3Tip1')}</li>
                      <li>{t('listProperty.step3Tip2')}</li>
                      <li>{t('listProperty.step3Tip3')}</li>
                    </ul>
                  </div>
                </div>

                <div className="scrollStep__fields">
                  <div className="scrollStep__card">
                    <div className="list-field">
                      <label>{t('listProperty.description')}</label>
                      <textarea placeholder="Describe your property — highlights, condition, nearby amenities..." value={form.description} onChange={e => update('description', e.target.value)} className={errors.description ? 'error' : ''} rows={6} />
                      {errors.description && <span className="list-field__err">{errors.description}</span>}
                    </div>
                    <div className="list-field">
                      <label>{t('listProperty.keyFeatures')} <span className="list-field__hint">{t('listProperty.commaSeparated')}</span></label>
                      <input type="text" placeholder="e.g. Garden, Garage, Pool, Balcony" value={form.features} onChange={e => update('features', e.target.value)} />
                    </div>
                    <div className="list-field">
                      <label>Energy Efficiency Rating</label>
                      <small className="list-field__hint">Cyprus EPC (A+ = best, G = worst). Skip if not yet assessed.</small>
                      <div className="listForm__epcGrid">
                        {[
                          { value: 'A+', color: '#00A651' },
                          { value: 'A',  color: '#50B848' },
                          { value: 'B',  color: '#AED136' },
                          { value: 'C',  color: '#FFF200' },
                          { value: 'D',  color: '#FDB813' },
                          { value: 'E',  color: '#F18A00' },
                          { value: 'F',  color: '#E94E1B' },
                          { value: 'G',  color: '#E30613' },
                        ].map(level => (
                          <button
                            key={level.value}
                            type="button"
                            onClick={() => setEpcRating(level.value === epcRating ? null : level.value)}
                            className={`listForm__epcBadge${epcRating === level.value ? ' is-selected' : ''}`}
                          >
                            {level.value}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="list-field">
                      <label>{t('listProperty.photos')} <span className="list-field__hint">{t('listProperty.photosUpTo')}</span></label>
                      {errors.photos && <span className="list-field__err">{errors.photos}</span>}
                      <div className="photo-strip">
                        {existingImages.map((url, i) => (
                          <div key={`existing-${i}`} className="photo-strip__thumb">
                            <img src={url} alt={`Photo ${i + 1}`} />
                            {i === 0 && <span className="photo-strip__main">{t('listProperty.mainPhoto')}</span>}
                            <button type="button" className="photo-strip__remove" onClick={() => removeExisting(i)}>
                              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                        ))}
                        {photos.map((p, i) => (
                          <div key={`new-${i}`} className="photo-strip__thumb">
                            <img src={p.url} alt={p.name} />
                            {existingImages.length === 0 && i === 0 && (
                              <span className="photo-strip__main">{t('listProperty.mainPhoto')}</span>
                            )}
                            <button type="button" className="photo-strip__remove" onClick={() => removePhoto(i)}>
                              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                        ))}
                        {totalPhotoCount < 10 && (
                          <label className="photo-strip__add">
                            <input type="file" accept="image/*" multiple onChange={handlePhotos} style={{ display: 'none' }} />
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                            <span>{totalPhotoCount === 0 ? t('listProperty.addPhotos') : t('listProperty.addMore')}</span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* STEP 4 — fields LEFT, info RIGHT */}
              <section
                className="scrollStep scrollStep--reversed"
                data-step="4"
                ref={el => stepRefs.current[3] = el}
              >
                <div className="scrollStep__dot"></div>

                <div className="scrollStep__fields">
                  <div className="scrollStep__card">
                    {profileNameMissing && (
                      <div
                        role="alert"
                        style={{
                          background: '#FAF9F6',
                          border: '1px solid #EFEEE9',
                          borderLeft: '3px solid #B8924A',
                          borderRadius: 8,
                          padding: '14px 16px',
                          marginBottom: 16,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0E0E10' }}>
                          Please complete your profile name before listing a property.
                        </div>
                        <div style={{ fontSize: 13, color: 'rgba(14,14,16,0.7)', lineHeight: 1.5 }}>
                          Your listing will display the name from your profile. Add a first and last name in your profile settings to continue.
                        </div>
                        <Link
                          to="/profile"
                          style={{
                            alignSelf: 'flex-start',
                            marginTop: 4,
                            background: '#0E0E10',
                            color: '#FAF9F6',
                            padding: '8px 14px',
                            borderRadius: 6,
                            textDecoration: 'none',
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          Go to Profile Settings
                        </Link>
                      </div>
                    )}
                    <div className="list-row">
                      <div className="list-field">
                        <label>{t('listProperty.yourName')}</label>
                        <div
                          aria-readonly="true"
                          style={{
                            background: '#EFEEE9',
                            border: '1px solid #EFEEE9',
                            borderRadius: 8,
                            padding: '12px 14px',
                            color: profileNameMissing ? 'rgba(14,14,16,0.4)' : 'rgba(14,14,16,0.7)',
                            fontSize: 14,
                            fontStyle: profileNameMissing ? 'italic' : 'normal',
                            cursor: 'not-allowed',
                            userSelect: 'text',
                            minHeight: 20,
                          }}
                        >
                          {profileName || 'No name set on profile yet'}
                        </div>
                        <small
                          className="list-field__hint"
                          style={{ display: 'block', marginTop: 6, color: 'rgba(14,14,16,0.6)' }}
                        >
                          This is the name from your profile.{' '}
                          <Link to="/profile" style={{ color: '#B8924A', fontWeight: 600, textDecoration: 'none' }}>
                            Change it in Profile Settings
                          </Link>
                          .
                        </small>
                        {errors.contactName && <span className="list-field__err">{errors.contactName}</span>}
                      </div>
                      <div className="list-field">
                        <label>{t('listProperty.phoneNumber')}</label>
                        <input type="tel" placeholder="+357 99 000 000" value={form.contactPhone} onChange={e => update('contactPhone', e.target.value)} className={errors.contactPhone ? 'error' : ''} />
                        {errors.contactPhone && <span className="list-field__err">{errors.contactPhone}</span>}
                      </div>
                    </div>
                    <div className="list-field">
                      <label>{t('listProperty.emailAddress')}</label>
                      <input type="email" placeholder="you@example.com" value={form.contactEmail} onChange={e => update('contactEmail', e.target.value)} className={errors.contactEmail ? 'error' : ''} />
                      {errors.contactEmail && <span className="list-field__err">{errors.contactEmail}</span>}
                    </div>
                  </div>
                </div>

                <div className="scrollStep__info">
                  <div className="scrollStep__number">Step 4 of 5</div>
                  <h2 className="scrollStep__title">{t('listProperty.step4Title')}</h2>
                  <p className="scrollStep__desc">{t('listProperty.step4Desc')}</p>
                  <div className="scrollStep__tips">
                    <div className="scrollStep__tipsLabel">{t('listProperty.step4TipsLabel')}</div>
                    <ul className="scrollStep__tipList">
                      <li>{t('listProperty.step4Tip1')}</li>
                      <li>{t('listProperty.step4Tip2')}</li>
                    </ul>
                  </div>
                </div>
              </section>

              <div className="scrollForm__finish">
                <button
                  className="scrollForm__finishBtn"
                  onClick={() => {
                    if (validateAllSteps()) {
                      setStep(5);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  {t('listProperty.continue')} →
                </button>
              </div>

            </div>
          )}

          {step === 5 && (
            <div className="list-step">
              <h3>{t('listProperty.step5Title')}</h3>
              <p className="preview__sub">{isEdit ? t('listProperty.editStep5Sub') : t('listProperty.step5Sub')}</p>
              {allPreviewPhotos.length > 0 && (
                <div className="preview__photos">
                  <div className="preview__photo-main-wrap">
                    <img src={allPreviewPhotos[0]} alt="main" className="preview__photo-main" />
                  </div>
                  {allPreviewPhotos.length > 1 && (
                    <div className="preview__photo-row">
                      {allPreviewPhotos.slice(1, 7).map((url, i) => (
                        <div key={i} className="preview__photo-thumb-wrap">
                          <img src={url} alt="" className="preview__photo-thumb" />
                        </div>
                      ))}
                      {allPreviewPhotos.length > 7 && <div className="preview__photo-more">+{allPreviewPhotos.length - 7}</div>}
                    </div>
                  )}
                </div>
              )}
              {[
                { title: t('listProperty.previewSectionProperty'), step: 1, fields: [
                  { label: t('listProperty.previewType'), value: `${form.listingType === 'sale' ? t('listProperty.forSale') : t('listProperty.forRent')} — ${form.propertyType}` },
                  { label: t('listProperty.previewTitle'), value: form.title },
                  { label: t('listProperty.previewPrice'), value: `€${Number(form.price).toLocaleString()}${form.listingType === 'rent' ? '/mo' : ''}` },
                ]},
                { title: t('listProperty.previewSectionLocation'), step: 2, fields: [
                  { label: t('listProperty.previewAddress'), value: `${form.address}${form.city ? ', ' + form.city : ''}${form.region ? ', ' + form.region : ''}` },
                  ...(form.plotSize ? [{ label: t('listProperty.previewPlotSize'), value: `${form.plotSize} m²` }] : []),
                  ...(form.size ? [{ label: t('listProperty.previewCoveredArea'), value: `${form.size} m²` }] : []),
                  ...(form.bedrooms ? [{ label: t('listProperty.previewBedsBaths'), value: `${form.bedrooms} ${t('listProperty.bed')} · ${form.bathrooms || '—'} ${t('listProperty.bath')}` }] : []),
                ]},
                { title: t('listProperty.previewSectionDetails'), step: 3, fields: [
                  { label: t('listProperty.description'), value: form.description },
                  ...(form.features ? [{ label: t('listProperty.keyFeatures'), value: form.features }] : []),
                  { label: t('listProperty.photos'), value: `${allPreviewPhotos.length} ${allPreviewPhotos.length !== 1 ? t('listProperty.photoPlural') : t('listProperty.photoSingular')}` },
                ]},
                { title: t('listProperty.previewSectionContact'), step: 4, fields: [
                  { label: t('listProperty.previewName'), value: profileName || '—' },
                  { label: t('listProperty.previewPhone'), value: form.contactPhone },
                  { label: t('listProperty.previewEmail'), value: form.contactEmail },
                ]},
              ].map(section => (
                <div key={section.title} className="preview__section" onClick={() => setStep(section.step)}>
                  <div className="preview__section-head">
                    <span className="preview__section-title">{section.title}</span>
                    <button className="preview__edit-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      {t('listProperty.edit')}
                    </button>
                  </div>
                  <div className="preview__fields">
                    {section.fields.map(f => (
                      <div key={f.label} className="preview__field"><span>{f.label}</span><strong>{f.value}</strong></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 5 && (
            <div className="list-nav">
              {isEdit && (
                <button className="list-nav__back" onClick={() => navigate('/my-listings')}>
                  {t('listProperty.back')}
                </button>
              )}
              <button className="list-nav__next" onClick={handleSubmit} disabled={submitting || profileNameMissing}>
                {submitBtnLabel}
              </button>
            </div>
          )}

          {step !== 5 && step > 1 && (
            <div className="list-nav" style={{ display: 'none' }}>
              <button className="list-nav__back" onClick={back}>{t('listProperty.back')}</button>
              <button className="list-nav__next" onClick={next}>{t('listProperty.continue')}</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
